// ═══════════════════════════════════════════════════════════════════════════════
// CLIENT PORTAL v3.0 — Secure OTP-Based Access with Real-Time RFQ Tracking
// EnergiPro Solutions — Controlled Client Access
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import {
  Lock, Clock, LogIn, User, Loader2, FileText, RefreshCw,
  CheckCircle2, AlertCircle, ChevronDown, ChevronUp,
  Download, Upload, Edit3, MessageSquare, Bell, ArrowRight,
  Shield, Activity, Zap, ExternalLink, X, Phone, Calendar, Mail
} from 'lucide-react';
import { InlineContactButtons } from '../../components/rfq/FloatingContactWidget';
import { generateWhatsAppLink, generateCalendarLink, CONTACT_CONFIG, trackContactInteraction } from '../../components/rfq/multiChannelContactEngine';
import {
  getStoredSession, requestOTP, verifyOTP, logout,
  startInactivityMonitor, stopInactivityMonitor, verifySession,
  AuthSession
} from '../../store/authStore';

import { BACKEND_URL } from '../../config/api';
const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

// ─── STATUS SYSTEM ───────────────────────────────────────────────────────────

type PortalStatus = 'Received' | 'Under Review' | 'Proposal Ready' | 'Awaiting Approval' | 'Confirmed';

const STATUS_FLOW: PortalStatus[] = [
  'Received',
  'Under Review',
  'Proposal Ready',
  'Awaiting Approval',
  'Confirmed',
];

function normalizeStatus(status: string): PortalStatus {
  const map: Record<string, PortalStatus> = {
    'Submitted': 'Received',
    'Under Review': 'Under Review',
    'Under Engineering Review': 'Under Review',
    'Proposal Sent': 'Proposal Ready',
    'Proposal Generated': 'Proposal Ready',
    'Awaiting Client Feedback': 'Awaiting Approval',
    'Pending Feedback': 'Awaiting Approval',
    'Approved': 'Confirmed',
    'Rejected': 'Confirmed',
    'Closed': 'Confirmed',
  };
  return map[status] || 'Received';
}

const STATUS_STYLES: Record<PortalStatus, { color: string; bg: string; border: string; icon: string }> = {
  'Received': { color: 'text-blue-300', bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: '📋' },
  'Under Review': { color: 'text-amber-300', bg: 'bg-amber-500/10', border: 'border-amber-500/30', icon: '🔧' },
  'Proposal Ready': { color: 'text-green-300', bg: 'bg-green-500/10', border: 'border-green-500/30', icon: '📄' },
  'Awaiting Approval': { color: 'text-orange-300', bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: '⏳' },
  'Confirmed': { color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/30', icon: '✓' },
};

// ─── TYPES ───────────────────────────────────────────────────────────────────

interface RFQRecord {
  id: number;
  referenceId: string;
  clientName: string;
  clientEmail: string;
  companyName: string;
  service: string;
  location: string;
  status: string;
  createdAt: number;
  parameters?: string;
  formSummary?: string;
  timeline?: string;
  notes?: string;
}

interface Notification {
  id: string;
  message: string;
  type: 'info' | 'success' | 'warning';
  timestamp: string;
  read: boolean;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function formatDate(timestamp: number): string {
  if (!timestamp) return '—';
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatTimeAgo(timestamp: number): string {
  if (!timestamp) return '—';
  const now = Date.now();
  const diff = now - timestamp * 1000;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function formatServiceLabel(service: string): string {
  if (!service) return '—';
  return service
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function getStageIndex(status: PortalStatus): number {
  return STATUS_FLOW.indexOf(status);
}

// ─── OTP LOGIN SCREEN ────────────────────────────────────────────────────────

type LoginStep = 'email' | 'otp';

const LoginScreen: React.FC<{ onAuthenticated: (session: AuthSession) => void }> = ({ onAuthenticated }) => {
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);

    const result = await requestOTP(email.trim().toLowerCase(), 'client');

    if (result.success) {
      setStep('otp');
      setSuccessMessage('A verification code has been sent to your email.');
    } else {
      setError(result.error || 'Unable to send verification code.');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) return;

    setLoading(true);
    setError(null);

    const result = await verifyOTP(email.trim().toLowerCase(), otp.trim());

    if (result.success && result.session) {
      onAuthenticated(result.session);
    } else {
      setError(result.error || 'Invalid verification code.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-md">
        <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-orange-500/10 border border-orange-500/30 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-orange-400" />
            </div>
            <h2 className="font-['Oswald'] text-2xl text-white uppercase tracking-wide">Client Portal</h2>
            <p className="text-slate-400 text-sm mt-2">Secure client access — restricted to authorized users only</p>
          </div>

          {step === 'email' ? (
            <form onSubmit={handleRequestOTP} className="space-y-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@company.com"
                    required
                    className="w-full bg-slate-800 border border-slate-600 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 transition-colors"
                  />
                </div>
                <p className="text-slate-500 text-xs mt-2">Enter the email used for your RFQ submissions</p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5 text-red-300 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {loading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              {successMessage && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-2.5 text-green-300 text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> {successMessage}
                </div>
              )}

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">Verification Code</label>
                <input
                  type="text"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  required
                  maxLength={6}
                  autoFocus
                  className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white text-center text-2xl font-mono tracking-[0.5em] placeholder:text-slate-600 placeholder:tracking-[0.5em] focus:outline-none focus:border-orange-500 transition-colors"
                />
                <p className="text-slate-500 text-xs mt-2">Enter the 6-digit code sent to {email}</p>
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5 text-red-300 text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || otp.length < 6}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </button>

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={() => { setStep('email'); setOtp(''); setError(null); setSuccessMessage(null); }}
                  className="text-slate-500 text-xs hover:text-slate-300 transition-colors"
                >
                  ← Use different email
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setLoading(true);
                    setError(null);
                    await requestOTP(email.trim().toLowerCase(), 'client');
                    setSuccessMessage('New code sent to your email.');
                    setOtp('');
                    setLoading(false);
                  }}
                  className="text-orange-400 text-xs hover:text-orange-300 transition-colors"
                >
                  Resend code
                </button>
              </div>
            </form>
          )}

          <div className="mt-8 pt-6 border-t border-slate-700/50">
            <p className="text-slate-500 text-xs text-center">
              No password required — secure verification via email
            </p>
            <div className="flex items-center justify-center gap-2 mt-3">
              <Lock className="w-3 h-3 text-slate-600" />
              <span className="text-slate-600 text-[10px] uppercase tracking-wider">End-to-end encrypted access</span>
            </div>
            <div className="mt-4 p-3 bg-slate-800/50 border border-slate-700/30 rounded-lg">
              <p className="text-slate-500 text-[10px] leading-relaxed text-center">
                Your data is protected under enterprise-grade security protocols. Only verified email holders with active RFQ submissions can access this portal. All sessions are monitored and auto-expire after inactivity.
              </p>
            </div>
            <div className="mt-4 flex items-center justify-center gap-4">
              <a href="https://wa.me/966552693829?text=I%20need%20help%20accessing%20the%20client%20portal" target="_blank" rel="noopener noreferrer" className="text-green-400 text-[10px] hover:text-green-300 transition-colors">WhatsApp Support</a>
              <span className="text-slate-700">|</span>
              <a href="mailto:sales@energiprogroup.com" className="text-orange-400 text-[10px] hover:text-orange-300 transition-colors">Email Support</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── PROGRESS TIMELINE ───────────────────────────────────────────────────────

const ProgressTimeline: React.FC<{ status: PortalStatus }> = ({ status }) => {
  const currentIndex = getStageIndex(status);
  const stages = [
    { label: 'Received', shortLabel: 'Received' },
    { label: 'Under Review', shortLabel: 'Review' },
    { label: 'Proposal Ready', shortLabel: 'Proposal' },
    { label: 'Awaiting Approval', shortLabel: 'Approval' },
    { label: 'Confirmed', shortLabel: 'Confirmed' },
  ];

  return (
    <div className="w-full">
      <div className="hidden md:flex items-center justify-between relative">
        <div className="absolute top-4 left-0 right-0 h-0.5 bg-slate-700" />
        <div
          className="absolute top-4 left-0 h-0.5 bg-orange-500 transition-all duration-700"
          style={{ width: `${currentIndex >= 4 ? 100 : (currentIndex / 4) * 100}%` }}
        />
        {stages.map((stage, i) => {
          const isComplete = i < currentIndex;
          const isCurrent = i === currentIndex;
          return (
            <div key={i} className="relative flex flex-col items-center z-10">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                isComplete ? 'bg-orange-500 border-orange-500' :
                isCurrent ? 'bg-orange-500/20 border-orange-500 ring-4 ring-orange-500/20' :
                'bg-slate-800 border-slate-600'
              }`}>
                {isComplete ? (
                  <CheckCircle2 className="w-4 h-4 text-white" />
                ) : (
                  <span className={`text-xs font-bold ${isCurrent ? 'text-orange-300' : 'text-slate-500'}`}>{i + 1}</span>
                )}
              </div>
              <p className={`mt-2 text-xs text-center max-w-[80px] ${
                isCurrent ? 'text-orange-300 font-semibold' :
                isComplete ? 'text-slate-300' : 'text-slate-500'
              }`}>
                {stage.label}
              </p>
            </div>
          );
        })}
      </div>
      <div className="md:hidden flex items-center gap-1">
        {stages.map((stage, i) => {
          const isComplete = i < currentIndex;
          const isCurrent = i === currentIndex;
          return (
            <div key={i} className="flex-1 flex flex-col items-center">
              <div className={`w-full h-1.5 rounded-full ${
                isComplete ? 'bg-orange-500' :
                isCurrent ? 'bg-orange-500/50' : 'bg-slate-700'
              }`} />
              <p className={`mt-1 text-[9px] text-center ${
                isCurrent ? 'text-orange-300 font-bold' :
                isComplete ? 'text-slate-400' : 'text-slate-600'
              }`}>
                {stage.shortLabel}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── RFQ DETAIL CARD ─────────────────────────────────────────────────────────

interface RFQDetailCardProps {
  rfq: RFQRecord;
  isExpanded: boolean;
  onToggle: () => void;
  onAction: (action: string, rfqId: number) => void;
}

const RFQDetailCard: React.FC<RFQDetailCardProps> = ({ rfq, isExpanded, onToggle, onAction }) => {
  const portalStatus = normalizeStatus(rfq.status || 'Submitted');
  const statusStyle = STATUS_STYLES[portalStatus];
  const currentIndex = getStageIndex(portalStatus);
  const hasProposal = currentIndex >= 2;
  const needsFeedback = portalStatus === 'Awaiting Approval' || portalStatus === 'Proposal Ready';
  const isClosed = portalStatus === 'Confirmed';

  return (
    <div className={`bg-[#111827] border rounded-xl overflow-hidden transition-all ${
      needsFeedback ? 'border-orange-500/40 ring-1 ring-orange-500/10' : 'border-slate-700/50'
    }`}>
      {needsFeedback && (
        <div className="h-1 bg-gradient-to-r from-orange-500 to-amber-500" />
      )}

      {!isClosed && (
        <div className="px-5 pt-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/5 border border-blue-500/15 rounded-md w-fit">
            <Shield className="w-3 h-3 text-blue-400" />
            <span className="text-blue-300 text-[10px] font-medium">Your request has been prioritized under our engineering workflow.</span>
          </div>
        </div>
      )}

      <div className="p-5 cursor-pointer" onClick={onToggle}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <span className="text-orange-300 font-mono text-sm font-bold">{rfq.referenceId}</span>
              <span className={`px-2.5 py-0.5 rounded-md border text-[10px] font-bold uppercase ${statusStyle.bg} ${statusStyle.border} ${statusStyle.color}`}>
                {statusStyle.icon} {portalStatus}
              </span>
              {needsFeedback && (
                <span className="px-2 py-0.5 rounded bg-orange-500/20 border border-orange-500/40 text-orange-300 text-[10px] font-bold animate-pulse">
                  ACTION REQUIRED
                </span>
              )}
            </div>
            <p className="text-white font-semibold text-sm">{formatServiceLabel(rfq.service)}</p>
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> Submitted: {formatDate(rfq.createdAt)}
              </span>
              <span className="flex items-center gap-1">
                <Activity className="w-3 h-3" /> Updated: {formatTimeAgo(rfq.createdAt)}
              </span>
            </div>
          </div>
          <button className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
            {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="px-5 pb-5 space-y-4 border-t border-slate-700/30 pt-4">
          {/* Progress Timeline */}
          <ProgressTimeline status={portalStatus} />

          {/* Details */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-slate-500 text-[10px] uppercase mb-1">Location</p>
              <p className="text-slate-200 text-sm">{rfq.location || '—'}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-slate-500 text-[10px] uppercase mb-1">Timeline</p>
              <p className="text-slate-200 text-sm">{rfq.timeline || '—'}</p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-3">
              <p className="text-slate-500 text-[10px] uppercase mb-1">Company</p>
              <p className="text-slate-200 text-sm">{rfq.companyName || '—'}</p>
            </div>
          </div>

          {/* Request Summary from formSummary */}
          {(() => {
            let formDetails: { label: string; value: string }[] = [];
            try { formDetails = JSON.parse(rfq.formSummary || '[]'); } catch {}
            if (formDetails.length === 0) {
              let params: Record<string, any> = {};
              try { params = JSON.parse(rfq.parameters || '{}'); } catch {}
              formDetails = Object.entries(params)
                .filter(([_, v]) => v)
                .map(([key, val]) => ({
                  label: key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
                  value: String(val),
                }));
            }
            if (formDetails.length === 0) return null;
            return (
              <div className="mt-4 pt-4 border-t border-slate-700/30">
                <p className="text-slate-400 text-xs font-semibold uppercase mb-2">Your Request Summary</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {formDetails.map((item, i) => (
                    <div key={i} className="bg-slate-800/50 rounded-lg p-2.5">
                      <p className="text-slate-500 text-[10px] uppercase">{item.label}</p>
                      <p className="text-white text-xs font-medium">{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 mt-4">
            {hasProposal && (
              <button className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 border border-green-500/30 text-green-300 rounded-lg text-xs hover:bg-green-500/20 transition-colors">
                <Download className="w-3.5 h-3.5" /> Download Proposal
              </button>
            )}
            {needsFeedback && (
              <>
                <button
                  onClick={() => onAction('confirm', rfq.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/10 border border-orange-500/30 text-orange-300 rounded-lg text-xs hover:bg-orange-500/20 transition-colors"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" /> Confirm Requirements
                </button>
                <button
                  onClick={() => onAction('revision', rfq.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700/50 border border-slate-600 text-slate-300 rounded-lg text-xs hover:bg-slate-700 transition-colors"
                >
                  <Edit3 className="w-3.5 h-3.5" /> Request Revision
                </button>
              </>
            )}
            <button
              onClick={() => onAction('upload', rfq.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700/50 border border-slate-600 text-slate-300 rounded-lg text-xs hover:bg-slate-700 transition-colors"
            >
              <Upload className="w-3.5 h-3.5" /> Upload Documents
            </button>
          </div>

          {/* Contact Options */}
          <div className="mt-4 pt-4 border-t border-slate-700/30">
            <p className="text-slate-500 text-[10px] uppercase mb-2">Need Help?</p>
            <div className="flex flex-wrap gap-2">
              <a
                href="mailto:sales@energiprogroup.com"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700/50 border border-slate-600 text-slate-300 rounded-lg text-xs hover:bg-slate-700 transition-colors"
              >
                <Mail className="w-3.5 h-3.5" /> Email Support
              </a>
              <a
                href="https://wa.me/966552693829"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 border border-green-500/30 text-green-300 rounded-lg text-xs hover:bg-green-500/20 transition-colors"
              >
                <Phone className="w-3.5 h-3.5" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ACTION MODAL ────────────────────────────────────────────────────────────

interface ActionModalProps {
  type: 'revision' | 'upload' | 'confirm';
  rfq: RFQRecord;
  onClose: () => void;
  onSubmit: (data: { type: string; message: string }) => void;
  isSubmitting: boolean;
}

const ActionModal: React.FC<ActionModalProps> = ({ type, rfq, onClose, onSubmit, isSubmitting }) => {
  const [message, setMessage] = useState('');

  const configs = {
    revision: { title: 'Request Revision', desc: 'Describe the changes needed:', submitLabel: 'Submit Revision' },
    upload: { title: 'Upload Documents', desc: 'Describe the documents you\'re providing:', submitLabel: 'Submit' },
    confirm: { title: 'Confirm Requirements', desc: 'Confirm your requirements are accurate:', submitLabel: 'Confirm' },
  };
  const c = configs[type];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">{c.title}</h3>
          <button onClick={onClose} className="p-1 text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-slate-400 text-sm mb-3">{c.desc}</p>
        <p className="text-orange-300 text-xs font-mono mb-3">Ref: {rfq.referenceId}</p>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Add your message..."
          className="w-full bg-slate-800 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500 min-h-[100px] text-sm"
        />
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-slate-400 text-sm hover:text-white transition-colors">
            Cancel
          </button>
          <button
            onClick={() => onSubmit({ type, message })}
            disabled={isSubmitting}
            className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {isSubmitting ? 'Submitting...' : c.submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── NOTIFICATION PANEL ──────────────────────────────────────────────────────

const NotificationPanel: React.FC<{ notifications: Notification[]; onDismiss: (id: string) => void }> = ({ notifications, onDismiss }) => {
  if (notifications.length === 0) return null;

  return (
    <div className="mb-6 space-y-2">
      {notifications.filter(n => !n.read).map(n => (
        <div key={n.id} className={`flex items-center justify-between p-3 rounded-lg border ${
          n.type === 'success' ? 'bg-green-500/5 border-green-500/20' :
          n.type === 'warning' ? 'bg-orange-500/5 border-orange-500/20' :
          'bg-blue-500/5 border-blue-500/20'
        }`}>
          <div className="flex items-center gap-3">
            {n.type === 'success' && <CheckCircle2 className="w-4 h-4 text-green-400" />}
            {n.type === 'warning' && <AlertCircle className="w-4 h-4 text-orange-400" />}
            {n.type === 'info' && <Bell className="w-4 h-4 text-blue-400" />}
            <div>
              <p className={`text-sm ${
                n.type === 'success' ? 'text-green-300' :
                n.type === 'warning' ? 'text-orange-300' : 'text-blue-300'
              }`}>{n.message}</p>
              <p className="text-slate-500 text-[10px] mt-0.5">{n.timestamp}</p>
            </div>
          </div>
          <button onClick={() => onDismiss(n.id)} className="p-1 text-slate-500 hover:text-white transition-colors">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};

// ─── EMPTY STATE ─────────────────────────────────────────────────────────────

const EmptyState: React.FC = () => (
  <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-12 text-center">
    <div className="w-16 h-16 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center mx-auto mb-4">
      <FileText className="w-7 h-7 text-slate-500" />
    </div>
    <h3 className="text-white text-lg font-medium mb-2">No RFQs submitted yet</h3>
    <p className="text-slate-400 text-sm max-w-md mx-auto">
      When you submit an RFQ through our system, it will appear here for real-time tracking.
    </p>
    <a href="/request-quote" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-all shadow-lg shadow-orange-500/20">
      Submit Your First RFQ
    </a>
  </div>
);

// ─── MAIN CLIENT PORTAL ──────────────────────────────────────────────────────

export const ClientPortal: React.FC = () => {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [rfqs, setRfqs] = useState<RFQRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [actionModal, setActionModal] = useState<{ type: 'revision' | 'upload' | 'confirm'; rfqId: number } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Check existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      const stored = getStoredSession();
      if (stored && stored.role === 'client') {
        const { valid } = await verifySession(stored.token);
        if (valid) {
          setSession(stored);
        }
      }
      setIsCheckingSession(false);
    };
    checkSession();
  }, []);

  // Start inactivity monitor when authenticated
  useEffect(() => {
    if (session) {
      startInactivityMonitor(() => {
        setSession(null);
        setRfqs([]);
        addNotification('Session expired due to inactivity. Please sign in again.', 'warning');
      });
      return () => stopInactivityMonitor();
    }
  }, [session]);

  // Fetch RFQs with token validation
  const fetchRFQs = useCallback(async (email: string) => {
    setLoading(true);
    setFetchError(null);
    try {
      const stored = getStoredSession();
      const headers: Record<string, string> = {};
      if (stored?.token) {
        headers['Authorization'] = `Bearer ${stored.token}`;
      }
      const res = await fetch(`${BACKEND_URL}/api/public/portal/rfqs?email=${encodeURIComponent(email)}`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to fetch' }));
        throw new Error(err.error || 'Failed to fetch RFQs');
      }
      const result = await res.json();
      setRfqs(result.data || []);
      setLastRefresh(new Date());
    } catch (err: any) {
      setFetchError('Unable to load your RFQs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh
  useEffect(() => {
    if (session) {
      fetchRFQs(session.email);
      const interval = setInterval(() => fetchRFQs(session.email), AUTO_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [session, fetchRFQs]);

  // Handle authentication
  const handleAuthenticated = (newSession: AuthSession) => {
    setSession(newSession);
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    stopInactivityMonitor();
    setSession(null);
    setRfqs([]);
  };

  // Handle client actions
  const handleAction = (action: string, rfqId: number) => {
    setActionModal({ type: action as 'revision' | 'upload' | 'confirm', rfqId });
  };

  const handleActionSubmit = async (data: { type: string; message: string }) => {
    if (!actionModal) return;
    setIsSubmitting(true);

    const statusMap: Record<string, string> = {
      revision: 'Under Review',
      upload: 'Under Review',
      confirm: 'Approved',
    };
    const newStatus = statusMap[data.type] || 'Under Review';

    try {
      const res = await fetch(`${BACKEND_URL}/api/public/rfq/${actionModal.rfqId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) throw new Error('Failed to update');

      setRfqs(prev => prev.map(r =>
        r.id === actionModal.rfqId ? { ...r, status: newStatus } : r
      ));

      const notificationMessages: Record<string, string> = {
        revision: 'Revision request submitted successfully. Our team will review shortly.',
        upload: 'Additional data submitted. Engineering team has been notified.',
        confirm: 'Requirement confirmed! Your RFQ has been approved.',
      };

      addNotification(notificationMessages[data.type] || 'Action completed.', data.type === 'confirm' ? 'success' : 'info');
      setActionModal(null);
    } catch {
      addNotification('Unable to complete action. Please try again.', 'warning');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addNotification = (message: string, type: 'info' | 'success' | 'warning') => {
    const notification: Notification = {
      id: `n-${Date.now()}`,
      message,
      type,
      timestamp: new Date().toLocaleString(),
      read: false,
    };
    setNotifications(prev => [notification, ...prev]);
  };

  const dismissNotification = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  // ─── RENDER ────────────────────────────────────────────────────────────────

  // Session check loading state
  if (isCheckingSession) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!session) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0a0f1e] py-16">
          <div className="max-w-7xl mx-auto px-4">
            <LoginScreen onAuthenticated={handleAuthenticated} />
          </div>
        </div>
      </Layout>
    );
  }

  // Counts
  const activeRFQs = rfqs.filter(r => normalizeStatus(r.status) !== 'Closed').length;
  const awaitingAction = rfqs.filter(r => {
    const s = normalizeStatus(r.status);
    return s === 'Awaiting Approval' || s === 'Proposal Ready';
  }).length;
  const completed = rfqs.filter(r => normalizeStatus(r.status) === 'Confirmed').length;

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0f1e] py-12">
        <div className="max-w-5xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-['Oswald'] text-3xl text-white uppercase tracking-wide">Client Portal</h1>
              <p className="text-slate-400 text-sm mt-1">Real-time RFQ tracking & interaction</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
                <Activity className="w-3.5 h-3.5 text-green-400 animate-pulse" />
                <span className="text-green-300 text-xs">Live</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-800/50 border border-slate-700 rounded-lg">
                <User className="w-4 h-4 text-orange-400" />
                <span className="text-slate-300 text-sm hidden sm:inline">{session.email}</span>
              </div>
              <button onClick={handleLogout} className="px-3 py-2 bg-slate-800 border border-slate-600 text-slate-400 rounded-lg text-sm hover:text-white transition-colors">
                Logout
              </button>
            </div>
          </div>

          {/* Welcome Onboarding Section */}
          {rfqs.length > 0 && (
            <div className="mb-6 bg-gradient-to-r from-[#111827] to-[#0f1f3a] border border-slate-700/30 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-orange-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white text-sm font-semibold mb-1">Welcome to Your Secure Portal</h3>
                  <p className="text-slate-400 text-xs leading-relaxed mb-3">
                    Track your RFQ submissions in real-time, download proposals, request revisions, and communicate directly with our engineering team — all in one secure place.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                      <CheckCircle2 className="w-3 h-3 text-green-400" />
                      <span>Real-time status tracking</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                      <CheckCircle2 className="w-3 h-3 text-green-400" />
                      <span>Download proposals & documents</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                      <CheckCircle2 className="w-3 h-3 text-green-400" />
                      <span>Direct engineering contact</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                      <CheckCircle2 className="w-3 h-3 text-green-400" />
                      <span>Auto-synced every 30 seconds</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notifications */}
          <NotificationPanel notifications={notifications} onDismiss={dismissNotification} />

          {/* Content */}
          {loading && rfqs.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Loader2 className="w-8 h-8 text-orange-400 animate-spin mx-auto mb-3" />
                <p className="text-slate-400 text-sm">Loading your RFQs...</p>
              </div>
            </div>
          ) : fetchError ? (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
              <p className="text-red-300 text-sm mb-4">{fetchError}</p>
              <button onClick={() => fetchRFQs(session.email)} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-600 text-white rounded-lg text-sm hover:bg-slate-700 transition-all">
                <RefreshCw className="w-4 h-4" /> Retry
              </button>
            </div>
          ) : rfqs.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {/* KPI Strip */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-[#111827] border border-slate-700/50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-white font-['Oswald']">{rfqs.length}</p>
                  <p className="text-slate-400 text-[10px] uppercase mt-1">Total RFQs</p>
                </div>
                <div className="bg-[#111827] border border-blue-500/20 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-300 font-['Oswald']">{activeRFQs}</p>
                  <p className="text-slate-400 text-[10px] uppercase mt-1">Active</p>
                </div>
                <div className="bg-[#111827] border border-orange-500/20 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-orange-300 font-['Oswald']">{awaitingAction}</p>
                  <p className="text-slate-400 text-[10px] uppercase mt-1">Needs Your Input</p>
                </div>
                <div className="bg-[#111827] border border-green-500/20 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-300 font-['Oswald']">{completed}</p>
                  <p className="text-slate-400 text-[10px] uppercase mt-1">Completed</p>
                </div>
              </div>

              {/* Refresh Bar */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-white font-bold text-lg flex items-center gap-2">
                  <Shield className="w-4 h-4 text-orange-400" /> Your RFQ Pipeline
                </h2>
                <div className="flex items-center gap-3">
                  <span className="text-slate-500 text-[10px]">
                    Last sync: {lastRefresh.toLocaleTimeString()}
                  </span>
                  <button
                    onClick={() => fetchRFQs(session.email)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-slate-400 hover:text-white text-xs transition-colors"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                  </button>
                </div>
              </div>

              {/* RFQ Cards */}
              <div className="space-y-4">
                {rfqs
                  .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
                  .map(rfq => (
                    <RFQDetailCard
                      key={rfq.id}
                      rfq={rfq}
                      isExpanded={expandedId === rfq.id}
                      onToggle={() => setExpandedId(expandedId === rfq.id ? null : rfq.id)}
                      onAction={handleAction}
                    />
                  ))}
              </div>
            </>
          )}

          {/* Action Modal */}
          {actionModal && (
            <ActionModal
              type={actionModal.type}
              rfq={rfqs.find(r => r.id === actionModal.rfqId)!}
              onClose={() => setActionModal(null)}
              onSubmit={handleActionSubmit}
              isSubmitting={isSubmitting}
            />
          )}

          {/* Security Notice */}
          <div className="mt-10 pt-6 border-t border-slate-700/20">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Lock className="w-3 h-3 text-slate-600" />
              <span className="text-slate-600 text-[10px] uppercase tracking-wider font-semibold">Data Security Statement</span>
            </div>
            <p className="text-slate-600 text-[10px] text-center max-w-xl mx-auto leading-relaxed">
              All data transmitted through this portal is encrypted and stored securely. Access is restricted to verified email holders only. Sessions auto-expire after 15 minutes of inactivity. Your RFQ data is isolated and cannot be accessed by other users. EnergiPro Solutions maintains enterprise-grade data protection standards aligned with ISO 27001 principles.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};
